import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { toast } from 'sonner';
import { 
  DollarSign, 
  Clock, 
  CheckCircle, 
  XCircle,
  AlertCircle,
  User,
  Calendar,
  Eye,
  ThumbsUp,
  ThumbsDown,
  Send,
  Check
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminPayoutManagement = () => {
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  
  // Modal states
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    fetchPayouts();
  }, [activeTab]);

  const fetchPayouts = async () => {
    try {
      setLoading(true);
      const token = Cookies.get('enakid_token');
      
      const statusFilter = activeTab === 'all' ? '' : activeTab;
      const response = await axios.get(`${API}/api/admin/payouts`, {
        params: { status: statusFilter || undefined },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPayouts(response.data || []);
    } catch (error) {
      console.error('Error fetching payouts:', error);
      toast.error('Failed to load payouts');
    } finally {
      setLoading(false);
    }
  };

  const handleApprovePayout = async (payoutId) => {
    try {
      const token = Cookies.get('enakid_token');
      await axios.put(
        `${API}/api/admin/payouts/${payoutId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Payout approved successfully');
      fetchPayouts();
    } catch (error) {
      console.error('Error approving payout:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve payout');
    }
  };

  const handleRejectPayout = async () => {
    try {
      if (!rejectReason.trim()) {
        toast.error('Please provide a rejection reason');
        return;
      }

      const token = Cookies.get('enakid_token');
      await axios.put(
        `${API}/api/admin/payouts/${selectedPayout.id}/reject`,
        {
          rejection_reason: rejectReason,
          admin_notes: adminNotes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Payout rejected successfully');
      setShowRejectModal(false);
      setRejectReason('');
      setAdminNotes('');
      fetchPayouts();
    } catch (error) {
      console.error('Error rejecting payout:', error);
      toast.error(error.response?.data?.detail || 'Failed to reject payout');
    }
  };

  const handleProcessPayout = async () => {
    try {
      const token = Cookies.get('enakid_token');
      const response = await axios.post(
        `${API}/api/admin/payouts/${selectedPayout.id}/process`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Payout marked for processing. Please complete the transfer via Chapa dashboard.');
      setShowProcessModal(false);
      fetchPayouts();
    } catch (error) {
      console.error('Error processing payout:', error);
      toast.error(error.response?.data?.detail || 'Failed to process payout');
    }
  };

  const handleMarkCompleted = async (payoutId) => {
    try {
      const token = Cookies.get('enakid_token');
      await axios.put(
        `${API}/api/admin/payouts/${payoutId}/mark-completed`,
        { admin_notes: 'Manually marked as completed' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Payout marked as completed');
      fetchPayouts();
    } catch (error) {
      console.error('Error marking payout as completed:', error);
      toast.error(error.response?.data?.detail || 'Failed to mark payout as completed');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending Review' },
      approved: { color: 'bg-blue-100 text-blue-800', icon: CheckCircle, label: 'Approved' },
      processing: { color: 'bg-purple-100 text-purple-800', icon: Send, label: 'Processing' },
      completed: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Completed' },
      rejected: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Rejected' },
      failed: { color: 'bg-red-100 text-red-800', icon: AlertCircle, label: 'Failed' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPayoutStats = () => {
    const stats = {
      pending: payouts.filter(p => p.status === 'pending').length,
      approved: payouts.filter(p => p.status === 'approved').length,
      processing: payouts.filter(p => p.status === 'processing').length,
      completed: payouts.filter(p => p.status === 'completed').length,
      total_amount: payouts.reduce((sum, p) => sum + (p.requested_amount || 0), 0)
    };
    return stats;
  };

  const stats = getPayoutStats();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Payout Management</h1>
        <p className="text-gray-500 mt-1">Review and process affiliate payout requests</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
            <Clock className="h-4 w-4 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Approved</CardTitle>
            <ThumbsUp className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{stats.approved}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Processing</CardTitle>
            <Send className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">{stats.processing}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completed</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.completed}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Amount</CardTitle>
            <DollarSign className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              ${stats.total_amount.toFixed(2)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payouts Table */}
      <Card>
        <CardHeader>
          <CardTitle>Payout Requests</CardTitle>
          <CardDescription>Review and process affiliate payout requests</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="pending">Pending ({stats.pending})</TabsTrigger>
              <TabsTrigger value="approved">Approved ({stats.approved})</TabsTrigger>
              <TabsTrigger value="processing">Processing ({stats.processing})</TabsTrigger>
              <TabsTrigger value="completed">Completed ({stats.completed})</TabsTrigger>
              <TabsTrigger value="all">All</TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab}>
              {payouts.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <DollarSign className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No {activeTab !== 'all' ? activeTab : ''} payout requests found.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {payouts.map((payout) => (
                    <div
                      key={payout.id}
                      className="p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          {/* Header */}
                          <div className="flex items-center gap-3 mb-3">
                            <p className="text-2xl font-bold text-gray-900">
                              ${payout.requested_amount?.toFixed(2)}
                            </p>
                            {getStatusBadge(payout.status)}
                          </div>

                          {/* Affiliate Info */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3">
                            <div>
                              <span className="text-gray-500">Affiliate:</span>
                              <p className="font-medium">{payout.affiliate?.email || 'N/A'}</p>
                              <p className="text-xs text-gray-500">{payout.affiliate?.affiliate_code}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Tier:</span>
                              <p className="font-medium capitalize">{payout.affiliate?.tier || 'N/A'}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Requested:</span>
                              <p className="font-medium">{formatDate(payout.requested_at)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Net Amount:</span>
                              <p className="font-medium text-green-600">${payout.net_amount?.toFixed(2)}</p>
                              <p className="text-xs text-gray-500">Fee: ${payout.processing_fee?.toFixed(2)}</p>
                            </div>
                          </div>

                          {/* Bank Details */}
                          <div className="bg-gray-50 rounded p-3 text-sm mb-3">
                            <p className="font-medium mb-1">Payment Details:</p>
                            <div className="grid grid-cols-2 gap-2 text-gray-700">
                              <div>
                                <span className="text-gray-500">Account Holder:</span> {payout.account_holder_name}
                              </div>
                              <div>
                                <span className="text-gray-500">Bank:</span> {payout.bank_name}
                              </div>
                              <div>
                                <span className="text-gray-500">Account Number:</span> {payout.account_number}
                              </div>
                              {payout.phone_number && (
                                <div>
                                  <span className="text-gray-500">Phone:</span> {payout.phone_number}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Notes */}
                          {payout.rejection_reason && (
                            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800 mb-3">
                              <span className="font-medium">Rejection Reason:</span> {payout.rejection_reason}
                            </div>
                          )}

                          {payout.admin_notes && (
                            <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-800 mb-3">
                              <span className="font-medium">Admin Notes:</span> {payout.admin_notes}
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col gap-2 ml-4">
                          {payout.status === 'pending' && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => handleApprovePayout(payout.id)}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                <ThumbsUp className="h-4 w-4 mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedPayout(payout);
                                  setShowRejectModal(true);
                                }}
                                className="border-red-300 text-red-600 hover:bg-red-50"
                              >
                                <ThumbsDown className="h-4 w-4 mr-1" />
                                Reject
                              </Button>
                            </>
                          )}

                          {payout.status === 'approved' && (
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedPayout(payout);
                                setShowProcessModal(true);
                              }}
                              className="bg-purple-600 hover:bg-purple-700"
                            >
                              <Send className="h-4 w-4 mr-1" />
                              Process
                            </Button>
                          )}

                          {payout.status === 'processing' && (
                            <Button
                              size="sm"
                              onClick={() => handleMarkCompleted(payout.id)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <Check className="h-4 w-4 mr-1" />
                              Mark Complete
                            </Button>
                          )}

                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedPayout(payout);
                              setShowDetailsModal(true);
                            }}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Details
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Payout Request Details</DialogTitle>
          </DialogHeader>
          {selectedPayout && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Status:</span>
                  <div className="mt-1">{getStatusBadge(selectedPayout.status)}</div>
                </div>
                <div>
                  <span className="text-gray-500">Amount:</span>
                  <p className="font-bold text-lg">${selectedPayout.requested_amount?.toFixed(2)}</p>
                </div>
                <div>
                  <span className="text-gray-500">Affiliate:</span>
                  <p className="font-medium">{selectedPayout.affiliate?.email}</p>
                </div>
                <div>
                  <span className="text-gray-500">Affiliate Code:</span>
                  <p className="font-medium">{selectedPayout.affiliate?.affiliate_code}</p>
                </div>
                <div>
                  <span className="text-gray-500">Requested At:</span>
                  <p className="font-medium">{formatDate(selectedPayout.requested_at)}</p>
                </div>
                {selectedPayout.processed_at && (
                  <div>
                    <span className="text-gray-500">Processed At:</span>
                    <p className="font-medium">{formatDate(selectedPayout.processed_at)}</p>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailsModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Payout Request</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this payout request.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reject_reason">
                Rejection Reason <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="reject_reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Explain why this payout is being rejected..."
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin_notes">Admin Notes (Optional)</Label>
              <Textarea
                id="admin_notes"
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Additional notes..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleRejectPayout} className="bg-red-600 hover:bg-red-700">
              Reject Payout
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Process Modal */}
      <AlertDialog open={showProcessModal} onOpenChange={setShowProcessModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Process Payout via Chapa</AlertDialogTitle>
            <AlertDialogDescription>
              <div className="space-y-3 text-sm">
                <p className="font-medium">
                  You are about to process a payout of ${selectedPayout?.requested_amount?.toFixed(2)}
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded p-3">
                  <p className="font-medium text-blue-900 mb-2">Manual Processing Required:</p>
                  <ol className="list-decimal list-inside space-y-1 text-blue-800">
                    <li>Log into your Chapa dashboard</li>
                    <li>Navigate to Transfers/Payouts section</li>
                    <li>Create a new transfer with these details:</li>
                  </ol>
                  <div className="mt-2 bg-white rounded p-2 text-xs font-mono">
                    <p><strong>Account:</strong> {selectedPayout?.account_holder_name}</p>
                    <p><strong>Bank:</strong> {selectedPayout?.bank_name}</p>
                    <p><strong>Account #:</strong> {selectedPayout?.account_number}</p>
                    <p><strong>Amount:</strong> ${selectedPayout?.net_amount?.toFixed(2)}</p>
                  </div>
                </div>
                <p className="text-yellow-700 bg-yellow-50 border border-yellow-200 rounded p-2">
                  ⚠️ After completing the transfer in Chapa dashboard, return here and mark this payout as completed.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleProcessPayout} className="bg-purple-600 hover:bg-purple-700">
              Mark as Processing
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminPayoutManagement;
